import React, { useState, useEffect, useCallback } from 'react';
import { X, Maximize2 } from 'lucide-react';
import {
    TavolaAnatomica, SelettoreTavola, tavolaIniziale, etichettaTavola, puntiDellaTavola,
} from './TavolaAnatomica';
import '../../styles/TavolaAnatomica.css';
import '../../styles/MappaZone.css';

/**
 * MappaZone
 * Schema tecnico su tavola anatomica: segna DOVE si e' intervenuti
 * durante questa procedura.
 *
 * Diverso dalle annotazioni dell'anamnesi, che descrivono lo stato del
 * paziente e restano valide nel tempo. Qui i punti appartengono alla
 * singola seduta: le zone trattate a marzo non devono comparire sullo
 * schema di settembre.
 *
 * OGNI PUNTO PORTA LA SUA TAVOLA (`mappa`), e si disegna solo su
 * quella: la tavola e il disegno stanno in TavolaAnatomica, che e' la
 * stessa dell'anamnesi. Fino al 21 settembre 2026 la tavola era una
 * sola per tutto il modulo e i punti restavano visibili cambiandola:
 * due punti sul viso comparivano a mezz'aria accanto alla figura
 * intera. I punti salvati prima di allora non hanno una tavola: si
 * mostrano nell'elenco come «da assegnare», con il modo per assegnarli
 * a quella in vista, e su nessuna tavola finche' qualcuno non lo fa -
 * assegnarli da soli sarebbe inventare un dato clinico.
 *
 * LA NUMERAZIONE E' UNICA per tutta la procedura, e la tabella sotto
 * mostra TUTTE le righe, con la colonna «Tavola»: una quantita' di
 * prodotto che sparisce dalla vista e' peggio di una riga in piu'.
 *
 * Non salva nulla da solo: restituisce { mappa, punti } al modulo che
 * lo contiene, come fanno MappaBotox e MappaFiller. `mappa` e' la
 * tavola in vista all'ultimo salvataggio, per riaprire dov'era.
 */

const MappaZone = ({ data, onChange, readOnly, sessoPaziente }) => {
    /* Modulo nuovo: la tavola parte dal sesso in anagrafica. Modulo
       gia' salvato: vale la tavola dell'ultimo lavoro. */
    const [mappa, setMappa] = useState(data?.mappa || tavolaIniziale(sessoPaziente));
    const [punti, setPunti] = useState(data?.punti || []);
    const [inModifica, setInModifica] = useState(null);
    const [ingrandita, setIngrandita] = useState(false);

    useEffect(() => {
        if (data) {
            if (data.mappa) setMappa(data.mappa);
            setPunti(data.punti || []);
        }
    }, [data]);

    // Esc chiude l'ingrandimento: e' il gesto che tutti si aspettano.
    const chiudiConEsc = useCallback((e) => {
        if (e.key === 'Escape') setIngrandita(false);
    }, []);

    useEffect(() => {
        if (!ingrandita) return;
        document.addEventListener('keydown', chiudiConEsc);
        return () => document.removeEventListener('keydown', chiudiConEsc);
    }, [ingrandita, chiudiConEsc]);

    const emetti = (nuovaMappa, nuoviPunti) => {
        if (onChange) onChange({ mappa: nuovaMappa, punti: nuoviPunti });
    };

    const cambiaMappa = (nuova) => {
        setMappa(nuova);
        emetti(nuova, punti);
    };

    const aggiungiPunto = ({ x, y }) => {
        if (readOnly) return;
        const nuovo = { id: Date.now(), mappa, x, y, prodotto: '', quantita: '' };
        const nuoviPunti = [...punti, nuovo];
        setPunti(nuoviPunti);
        setInModifica(nuovo.id);
        emetti(mappa, nuoviPunti);
    };

    const aggiornaPunto = (id, campo, valore) => {
        const nuoviPunti = punti.map((p) => (p.id === id ? { ...p, [campo]: valore } : p));
        setPunti(nuoviPunti);
        emetti(mappa, nuoviPunti);
    };

    const rimuoviPunto = (id) => {
        const nuoviPunti = punti.filter((p) => p.id !== id);
        setPunti(nuoviPunti);
        if (inModifica === id) setInModifica(null);
        emetti(mappa, nuoviPunti);
    };

    const totale = punti.reduce((s, p) => s + (parseFloat(String(p.quantita).replace(',', '.')) || 0), 0);
    const inVista = puntiDellaTavola(punti, mappa).length;
    const daAssegnare = punti.filter((p) => !p.mappa).length;

    const rendiPunto = (p, i) => (
        <span
            className={`mz-punto ${inModifica === p.id ? 'mz-punto-attivo' : ''}`}
            onClick={(e) => { e.stopPropagation(); if (!readOnly) setInModifica(p.id); }}
            title={p.prodotto ? `${p.prodotto}${p.quantita ? ` · ${p.quantita}` : ''}` : `Punto ${i + 1}`}
        >
            {i + 1}
        </span>
    );

    /* La tavola compare due volte, in linea e ingrandita: stesso
       componente, stessi punti, stesse percentuali dell'immagine. */
    const tavola = (grande) => (
        <div className={`mz-tavola ${readOnly ? 'mz-sola-lettura' : ''} ${grande ? 'mz-tavola-grande' : ''}`}>
            <TavolaAnatomica mappa={mappa} punti={punti} rendiPunto={rendiPunto}
                             onClick={readOnly ? null : aggiungiPunto} />
        </div>
    );

    return (
        <div className="mz-wrap">

            <div className="mz-comandi">
                {!readOnly && <SelettoreTavola mappa={mappa} onCambia={cambiaMappa} className="mz-" />}
                <button className="mz-ingrandisci" onClick={() => setIngrandita(true)}
                        title="Apri la tavola a schermo intero">
                    <Maximize2 size={14} strokeWidth={1.8} aria-hidden="true" />
                    Ingrandisci
                </button>
                <span className="mz-conteggio">
                    {inVista === 0 ? 'Nessun punto su questa tavola'
                        : inVista === 1 ? '1 punto su questa tavola' : `${inVista} punti su questa tavola`}
                    {punti.length > inVista && ` · ${punti.length} in tutto`}
                </span>
            </div>

            {tavola(false)}

            <div className="mz-elenco">
                {punti.length === 0 ? (
                    <p className="mz-vuoto">
                        {readOnly
                            ? 'Nessuna zona segnata.'
                            : 'Tocca la tavola nel punto in cui sei intervenuto.'}
                    </p>
                ) : (
                    <>
                        {daAssegnare > 0 && (
                            <p className="mz-avviso">
                                {daAssegnare === 1 ? 'Un punto è' : `${daAssegnare} punti sono`} senza tavola:
                                {' '}segnati prima del 21 settembre 2026, quando la tavola non veniva
                                registrata. Non si disegnano finché non li assegni tu a quella giusta.
                            </p>
                        )}
                        <table className="mz-tabella">
                            <thead>
                                <tr>
                                    <th>N.</th>
                                    <th>Tavola</th>
                                    <th>Prodotto o tecnica</th>
                                    <th>Quantità</th>
                                    {!readOnly && <th />}
                                </tr>
                            </thead>
                            <tbody>
                                {punti.map((p, i) => (
                                    <tr key={p.id}
                                        className={`${inModifica === p.id ? 'mz-riga-attiva' : ''} ${p.mappa === mappa ? '' : 'mz-riga-altrove'}`}>
                                        <td className="mz-numero">{i + 1}</td>
                                        <td className="mz-tavola-cella">
                                            {p.mappa ? (
                                                <button type="button" className="mz-vai" disabled={p.mappa === mappa}
                                                        title={p.mappa === mappa ? 'In vista' : 'Mostra questa tavola'}
                                                        onClick={() => cambiaMappa(p.mappa)}>
                                                    {etichettaTavola(p.mappa)}
                                                </button>
                                            ) : readOnly ? (
                                                <span className="mz-da-assegnare">Tavola: da assegnare</span>
                                            ) : (
                                                <button type="button" className="mz-vai mz-da-assegnare"
                                                        title="Assegna questo punto alla tavola in vista"
                                                        onClick={() => aggiornaPunto(p.id, 'mappa', mappa)}>
                                                    Da assegnare → {etichettaTavola(mappa)}
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            {readOnly ? (p.prodotto || '—') : (
                                                <input
                                                    type="text"
                                                    value={p.prodotto}
                                                    onChange={(e) => aggiornaPunto(p.id, 'prodotto', e.target.value)}
                                                    onFocus={() => setInModifica(p.id)}
                                                    placeholder="Es. acido ialuronico"
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {readOnly ? (p.quantita || '—') : (
                                                <input
                                                    type="text"
                                                    value={p.quantita}
                                                    onChange={(e) => aggiornaPunto(p.id, 'quantita', e.target.value)}
                                                    onFocus={() => setInModifica(p.id)}
                                                    placeholder="Es. 0,5 ml"
                                                    className="mz-quantita"
                                                />
                                            )}
                                        </td>
                                        {!readOnly && (
                                            <td>
                                                <button className="mz-rimuovi"
                                                        onClick={() => rimuoviPunto(p.id)}
                                                        title="Rimuovi il punto">
                                                    <X size={14} strokeWidth={2} aria-hidden="true" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {totale > 0 && (
                            <div className="mz-totale">
                                <span>Totale</span>
                                <strong>{totale.toFixed(1).replace('.', ',')}</strong>
                            </div>
                        )}
                    </>
                )}
            </div>

            {ingrandita && (
                <div className="mz-sovrapposizione" onClick={() => setIngrandita(false)}>
                    <div className="mz-sovrapposizione-corpo" onClick={(e) => e.stopPropagation()}>
                        <div className="mz-sovrapposizione-testa">
                            <span>
                                {etichettaTavola(mappa)}
                                {!readOnly && ' — tocca per aggiungere un punto'}
                            </span>
                            <button onClick={() => setIngrandita(false)} aria-label="Chiudi">
                                <X size={18} strokeWidth={1.8} />
                            </button>
                        </div>
                        {tavola(true)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MappaZone;
